import React, { useMemo, useRef, useState, useEffect } from 'react';
import CommonModal from '../../../Components/CommonModal';
import CommonLoader from '../../../Components/CommonLoader';
import NovaAiAssist from './NovaAiAssist';
import { add_new_risk, update_risk_registry, risk_portal_roles_list } from '../../../services';

// Sentinel option value for "enter the email manually" in the Risk Owner select.
const OWNER_OTHER = '__other__';

// Unwrap the SbomApiIntercepter response envelope — same shape helper
// RiskPortal.js uses (kept local so this form has no dependency on it).
const extract = (res) =>
    res?.data?.response_data?.data || res?.data?.data ||
    res?.statusCode?.response_data?.data || res?.statusCode?.data || null;

const extractErrorMessage = (e) => {
    const r = e?.response?.data;
    if (!r) return e?.message || 'Network error - could not reach the server';
    const candidates = [r?.error?.message, r?.response_data?.message, r?.error_msg, r?.detail, r?.message];
    return candidates.find(m => typeof m === 'string' && m.trim()) || e?.message || 'Failed to save risk';
};

// Fallback categories when the org has none configured yet — mirrors the
// hardcoded list the original modal fell back to.
const DEFAULT_CATEGORY_OPTIONS = [
    { value: 'financial', label: 'Financial' },
    { value: 'operational', label: 'Operational' },
    { value: 'technical', label: 'Technical' },
    { value: 'compliance_legal', label: 'Compliance / Legal' },
    { value: 'environmental', label: 'Environmental' },
    { value: 'reputational', label: 'Reputational' },
    { value: 'strategic', label: 'Strategic' },
];

const LIKELIHOOD_OPTIONS = [
    { value: 1, label: '1 - Rare' },
    { value: 2, label: '2 - Unlikely' },
    { value: 3, label: '3 - Possible' },
    { value: 4, label: '4 - Likely' },
    { value: 5, label: '5 - Almost Certain' },
];

const IMPACT_OPTIONS = [
    { value: 1, label: '1 - Insignificant' },
    { value: 2, label: '2 - Minor' },
    { value: 3, label: '3 - Moderate' },
    { value: 4, label: '4 - Major' },
    { value: 5, label: '5 - Catastrophic' },
];

const STATE_OPTIONS = [
    { value: 'open', label: 'Open' },
    { value: 'identified', label: 'Identified' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'closed', label: 'Closed' },
    { value: 'transferred', label: 'Transferred' },
];

// A risk's business_unit / project may come back as a bare id or a nested
// object — normalise to the id so it matches the dropdown option values.
const idOf = (v) => (v && typeof v === 'object' ? v.id : v);

export default function AddRisk({
    show,
    onHide,
    onSaved,
    onCreated,
    onError,
    editingRisk = null,
    categories = [],
    businessUnits = [],
    projects = [],
    role,
    defaultOwnerEmail = '',
    // Inline mode: render the exact same form as an on-screen card (no modal),
    // e.g. embedded in the Assessment workflow stage. `submitText` /
    // `submitLoadingText` relabel the single primary button (no Cancel).
    inline = false,
    submitText,
    submitLoadingText,
}) {
    const [busy, setBusy] = useState(false);
    // Imperative handle onto CommonModal's form (setValue / getValues) so the
    // NOVA AI assist panel can read the typed description and write results back.
    const formApi = useRef(null);
    const isEdit = !!editingRisk;
    const done = onSaved || onCreated;
    const fail = (msg) => { onError?.(msg); setBusy(false); };

    const categoryOptions = useMemo(() => (
        categories && categories.length > 0
            ? categories.filter(c => c.is_active !== false).map(c => ({ value: c.name || c.id, label: c.name }))
            : DEFAULT_CATEGORY_OPTIONS
    ), [categories]);

    const businessUnitOptions = useMemo(() => ([
        { value: '', label: 'Organization-level (no BU)' },
        ...businessUnits.filter(b => b.is_active).map(b => ({
            value: b.id,
            label: `${b.parent_id ? '↳ ' : ''}${b.name}`,
        })),
    ]), [businessUnits]);

    const projectOptions = useMemo(() => ([
        { value: '', label: 'Not project-bound' },
        ...projects.filter(p => p.is_active !== false).map(p => ({
            value: p.id,
            label: `${p.name}`,
        })),
    ]), [projects]);

    // Risk Owner is chosen from the users holding the `risk_owner` role
    // (risk-portal/roles/), with an "Other" option to type an email manually.
    const [ownerUsers, setOwnerUsers] = useState([]); // [{ email, name }]
    const [ownerOther, setOwnerOther] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const d = extract(await risk_portal_roles_list());
                const list = Array.isArray(d?.roles) ? d.roles : Array.isArray(d?.users) ? d.users : [];
                const owners = list
                    .filter(u => u && u.email && String(u.role || '').toLowerCase() === 'risk_owner' && u.is_active !== false)
                    .map(u => ({
                        email: u.email,
                        name: `${u.first_name || ''} ${u.last_name || ''}`.trim(),
                    }));
                // De-dupe by email.
                const seen = new Set();
                const unique = owners.filter(o => (seen.has(o.email) ? false : seen.add(o.email)));
                if (!cancelled) {
                    setOwnerUsers(unique);
                    // No role users to pick from → go straight to manual entry.
                    if (unique.length === 0) setOwnerOther(true);
                }
            } catch (e) { /* leave empty — the "Other" option still lets them type an email */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const ownerOptions = useMemo(() => ([
        ...ownerUsers.map(o => ({ value: o.email, label: o.name ? `${o.name} (${o.email})` : o.email })),
        { value: OWNER_OTHER, label: 'Other (enter manually)…' },
    ]), [ownerUsers]);

    // If the pre-filled owner (e.g. editing an existing risk) isn't one of the
    // role users, drop into manual-entry mode so the value still shows.
    useEffect(() => {
        if (!ownerUsers.length) return;
        const cur = formApi.current?.getValues?.('risk_owner');
        if (cur && cur !== OWNER_OTHER && !ownerUsers.some(o => o.email === cur)) {
            setOwnerOther(true);
        }
    }, [ownerUsers]);

    const fields = [
        {
            name: 'risk', label: 'Risk Description', type: 'textarea', rows: 3, required: 'Risk description is required',
            placeholder: 'What could go wrong? Describe the risk event...',
        },
        { name: 'threat', label: 'Threat', type: 'text', half: true,  placeholder: 'What could cause this risk?' },
        { name: 'vulnerability', label: 'Vulnerability', type: 'text', half: true,  placeholder: 'What weakness could be exploited?' },
        { name: 'risk_type', label: 'Category', type: 'select', half: true, required: true, placeholder: 'Select category...', options: categoryOptions },
        // Pick a risk owner from the role users, or switch to manual email entry
        // via the "Other" option.
        ownerOther
            ? {
                name: 'risk_owner', label: 'Risk Owner Email', type: 'email', half: true,
                required: 'Risk owner email is required',
                placeholder: role === 'risk_owner' ? 'your.email@company.com' : 'who will own this risk?',
                suffixButton: {
                    text: 'Pick from list', variant: 'secondary',
                    onClick: () => { setOwnerOther(false); formApi.current?.setValue?.('risk_owner', '', { shouldValidate: false }); },
                },
            }
            : {
                name: 'risk_owner', label: 'Risk Owner Email', type: 'select', half: true,
                required: 'Risk owner is required',
                placeholder: 'Select risk owner…',
                options: ownerOptions,
                onChange: (v) => {
                    if (v === OWNER_OTHER) {
                        setOwnerOther(true);
                        formApi.current?.setValue?.('risk_owner', '', { shouldValidate: false });
                    }
                },
            },
        // min = today so past dates can't be picked (only today / future).
        { name: 'target_date', label: 'Risk Identification Date', type: 'date', half: true, min: new Date().toLocaleDateString('en-CA') },
        // State is only editable when updating an existing risk (create is always 'open').
        ...(isEdit ? [{ name: 'state', label: 'State', type: 'select', half: true, options: STATE_OPTIONS }] : []),
        { name: 'business_unit', label: 'Business Unit', type: 'select', half: true, options: businessUnitOptions },
        { name: 'project', label: 'Project', type: 'select', half: true, options: projectOptions },
        { name: 'likelihood', label: 'Likelihood 1-5', type: 'select', half: true, required: true, options: LIKELIHOOD_OPTIONS },
        { name: 'impact', label: 'Impact 1-5', type: 'select', required: true, half: true, options: IMPACT_OPTIONS },
    ];

    const defaultValues = useMemo(() => {
        if (isEdit) {
            const bu = idOf(editingRisk.business_unit ?? editingRisk.business_unit_id);
            const pr = idOf(editingRisk.project ?? editingRisk.project_id);
            return {
                risk: editingRisk.risk || '',
                threat: editingRisk.threat || '',
                vulnerability: editingRisk.vulnerability || '',
                risk_type: editingRisk.risk_type || '',
                risk_owner: editingRisk.risk_owner || '',
                target_date: editingRisk.target_date ? String(editingRisk.target_date).split('T')[0] : '',
                state: editingRisk.state || 'open',
                business_unit: bu != null && bu !== '' ? Number(bu) : '',
                project: pr != null && pr !== '' ? Number(pr) : '',
                likelihood: editingRisk.likelihood || 1,
                impact: editingRisk.impact || 1,
            };
        }
        return {
            risk: '', threat: '', vulnerability: '', risk_type: '',
            risk_owner: defaultOwnerEmail || '', target_date: '',
            business_unit: '', project: '', likelihood: 1, impact: 1,
        };
    }, [isEdit, editingRisk, defaultOwnerEmail]);

    const handleSubmit = async (data) => {
        setBusy(true);
        try {
            const common = {
                risk: (data.risk || '').trim(),
                threat: data.threat,
                vulnerability: data.vulnerability,
                risk_type: data.risk_type,
                risk_owner: (data.risk_owner || '').trim(),
                target_date: data.target_date || null,
                likelihood: Number(data.likelihood) || 1,
                impact: Number(data.impact) || 1,
                business_unit: data.business_unit ? parseInt(data.business_unit, 10) : null,
                project: data.project ? parseInt(data.project, 10) : null,
            };

            let res;
            if (isEdit) {
                // Preserve the risk's existing treatment fields (not edited here).
                const params = {
                    ...common,
                    state: data.state || 'open',
                    treatment_option: editingRisk.treatment_option || '',
                    treatment_plan: editingRisk.treatment_plan || '',
                };
                res = await update_risk_registry({ id: editingRisk.id, params });
            } else {
                res = await add_new_risk({ ...common, state: 'open' });
            }

            // Detect the 200-with-status:0 error envelope (both the ApiIntercepter
            // and AIGApi response shapes), same as the original.
            const body = res?.data || res?.statusCode || {};
            if (body?.status === 0) {
                fail(body?.error?.message || `Failed to ${isEdit ? 'update' : 'create'} risk`);
                return;
            }
            const d = extract(res);
            setBusy(false);
            done?.(d);
        } catch (e) {
            fail(extractErrorMessage(e));
        }
    };

    return (
        <CommonModal
            show={show}
            onHide={onHide}
            mid
            inline={inline}
            title={isEdit ? `Edit Risk${editingRisk.risk_id ? ` ${editingRisk.risk_id}` : ''}` : 'Identify Risk'}
            subtitle={isEdit
                ? 'Update this risk’s identification details, scoring and state.'
                : 'Stage 1: Identification. Assessment, treatment, and acceptance are handled in subsequent workflow stages.'}
            fields={fields}
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
            formRef={formApi}
            buttons={inline
                ? [{
                    text: submitText || (isEdit ? 'Save Changes' : 'Create Risk'), variant: 'primary', type: 'submit',
                    loading: busy, loadingText: submitLoadingText || (isEdit ? 'Saving…' : 'Creating…'),
                }]
                : [
                    { text: 'Cancel', variant: 'secondary', onClick: onHide, disabled: busy },
                    {
                        text: isEdit ? 'Save Changes' : 'Create Risk', variant: 'primary', type: 'submit',
                        loading: busy, loadingText: isEdit ? 'Saving…' : 'Creating…',
                    },
                ]}
        >
            {/* NOVA AI assist — analyses the typed description and auto-fills the
                form (category, ISO statement, threat/vulnerability, scores) plus
                a duplicate-risk check. Reads/writes the form via `formApi`. */}
            <NovaAiAssist formApi={formApi} categoryOptions={categoryOptions} />

            {/* Loader covers the whole modal while the submit is in-flight. */}
            {busy && <CommonLoader overlay label={isEdit ? 'Saving changes…' : 'Creating risk…'} />}
        </CommonModal>
    );
}
